import { IconProp } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { FontSize, Text } from "../../../components/Text";
import { Breakpoints } from "../../../theme/Breakpoints";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";

export const DashboardColumns = styled.div<{ $ratio?: string }>`
  display: grid;
  gap: ${Spacing.m};
  grid-template-columns: ${({ $ratio }) =>
    $ratio || "repeat(2, minmax(0, 1fr))"};
  align-items: stretch;

  @media (max-width: ${Breakpoints.DESKTOP}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  min-width: 0;
  height: 100%;
  padding: ${Spacing.m};
  border-radius: ${Rounded.xl};
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.s};
  min-height: 2rem;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${FontSize.l};
  font-weight: bold;
  line-height: 1.3;
  color: ${({ theme }) => theme.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.s};
  flex: none;
  font-size: ${FontSize.s};
  font-weight: 500;
  color: ${({ theme }) => theme.main};

  &:hover {
    color: ${({ theme }) => theme.main};
    text-decoration: underline;
  }
`;

const CtaLabel = styled.span`
  @media (max-width: ${Breakpoints.PHONE}) {
    display: none;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

type DashboardPanelProps = {
  title: string;
  subtitle?: string;
  cta?: { to: string; label: string; icon: IconProp; testId?: string };
  dataTestId?: string;
  children: React.ReactNode;
};

export const DashboardPanel: React.FC<DashboardPanelProps> = ({
  title,
  subtitle,
  cta,
  dataTestId,
  children,
}) => (
  <Card data-testid={dataTestId}>
    <Header>
      <Heading>
        <Title title={title}>{title}</Title>
        {subtitle ? (
          <Text fontSize="s" color="darkerSub">
            {subtitle}
          </Text>
        ) : null}
      </Heading>
      {cta ? (
        <CtaLink
          to={cta.to}
          className={cta.testId}
          aria-label={cta.label}
          title={cta.label}
        >
          <AtiraIcon icon={cta.icon} />
          <CtaLabel>{cta.label}</CtaLabel>
        </CtaLink>
      ) : null}
    </Header>
    <Body>{children}</Body>
  </Card>
);
